import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Sparkle, Drop, User, Phone, CheckCircle, CurrencyEur, Clock, Trash, EyeSlash } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useConfirmDialog } from './ConfirmDialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter27 F — Only TWO statuses survive: Në pritje (pending|confirmed) + Kryer (completed).
// Any legacy `in_progress` is migrated to `pending` at backend startup.
const isActiveStatus = (s) => s === 'pending' || s === 'confirmed' || s === 'in_progress';
const STATUS_LABEL = (s) => (s === 'completed' ? 'Kryer' : 'Në pritje');
const STATUS_COLOR = (s) => (s === 'completed'
  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
  : 'bg-amber-500/15 text-amber-300 border-amber-500/30');

// Iter27 G2 — five intervals (30m / 1h / 2h / 3h / 4h).
const SLOT_CHOICES = [
  { m: 30, label: '30m' },
  { m: 60, label: '1h' },
  { m: 120, label: '2h' },
  { m: 180, label: '3h' },
  { m: 240, label: '4h' },
];

export default function AdminBookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slotMinutes, setSlotMinutes] = useState(60);
  const [workingHours, setWorkingHours] = useState({ open_hour: 7, close_hour: 19, working_days: [1, 2, 3, 4, 5, 6] });
  const [loading, setLoading] = useState(true);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [selectedDay, setSelectedDay] = useState(null); // Iter27 G3 — clickable date filter
  const [hideCompleted, setHideCompleted] = useState(true); // Iter27 G4
  const [premiumCurrency, setPremiumCurrency] = useState('EUR'); // for the pay-dialog label
  const { ConfirmDialog, confirm } = useConfirmDialog();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s, sc, wh, pc] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/manager/assignable-staff`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/business-settings/booking-slot`, { withCredentials: true }).catch(() => ({ data: { booking_slot_minutes: 60 } })),
        axios.get(`${API}/business-settings/working-hours`, { withCredentials: true }).catch(() => ({ data: { open_hour: 7, close_hour: 19, working_days: [1, 2, 3, 4, 5, 6] } })),
        axios.get(`${API}/business-settings/premium-currency`, { withCredentials: true }).catch(() => ({ data: { premium_currency: 'EUR' } })),
      ]);
      setBookings(b.data || []);
      setStaff(s.data || []);
      setSlotMinutes(sc.data?.booking_slot_minutes || 60);
      setWorkingHours({
        open_hour: wh.data?.open_hour ?? 7,
        close_hour: wh.data?.close_hour ?? 19,
        working_days: wh.data?.working_days || [1, 2, 3, 4, 5, 6],
      });
      setPremiumCurrency(pc.data?.premium_currency || 'EUR');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSlot = async (next) => {
    try {
      await axios.put(`${API}/business-settings/booking-slot`, { booking_slot_minutes: next }, { withCredentials: true });
      setSlotMinutes(next);
      toast.success('OK');
    } catch { toast.error('Gabim'); }
  };

  const saveWorkingHours = async (patch) => {
    try {
      const r = await axios.put(`${API}/business-settings/working-hours`, patch, { withCredentials: true });
      setWorkingHours(r.data);
      toast.success('OK');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gabim'); }
  };

  const completeBooking = async (b) => {
    try {
      await axios.patch(`${API}/bookings/${b.booking_id}/status`, { status: 'completed' }, { withCredentials: true });
      toast.success('U përditësua');
      fetchAll();
    } catch { toast.error('Gabim'); }
  };

  const reassign = async (b, newStaffId) => {
    try {
      await axios.put(`${API}/bookings/${b.booking_id}/assign`, { staff_id: newStaffId || null }, { withCredentials: true });
      toast.success('U caktua');
      fetchAll();
    } catch { toast.error('Gabim'); }
  };

  const deleteBooking = async (b) => {
    const ok = await confirm({
      title: 'Je i sigurt?',
      description: `Ky veprim nuk kthehet — rezervimi i ${b.customer_name} (${b.service_name_snapshot}) do të fshihet.`,
      confirmText: 'Fshi', cancelText: 'Anulo', danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/bookings/${b.booking_id}`, { withCredentials: true });
      toast.success('U fshi');
      fetchAll();
    } catch { toast.error('Gabim'); }
  };

  const openPay = (b) => {
    setPayTarget(b);
    setPayAmount(String(b.price_eur_snapshot ?? ''));
  };

  // Iter27 E — currency is now auto-resolved from business-settings; the dialog
  // shows only the amount input and the locked currency label.
  const submitPay = async () => {
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) { toast.error('Vlerë e pasaktë'); return; }
    try {
      await axios.put(`${API}/bookings/${payTarget.booking_id}/mark-paid-v2`,
        { amount }, { withCredentials: true });
      toast.success('U shënua si paguar');
      setPayTarget(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gabim'); }
  };

  // Iter27 G — Filter sequence: cancelled/no-show out, then optional hide-completed, then date filter.
  const baseVisible = bookings.filter((b) => !['cancelled', 'no_show'].includes(b.status));
  const respHideCompleted = hideCompleted ? baseVisible.filter((b) => b.status !== 'completed') : baseVisible;

  // 7-day window
  const weekDays = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(today); d.setDate(today.getDate() + i); return d;
    });
  }, []);
  const dayKey = (d) => d.toISOString().slice(0, 10);
  const visible = selectedDay
    ? respHideCompleted.filter((b) => (b.scheduled_at || '').slice(0, 10) === selectedDay)
    : respHideCompleted;
  const todayKey = dayKey(weekDays[0]);

  if (loading) return <p className="text-xs text-white/40 px-2">Duke ngarkuar…</p>;

  return (
    <div className="space-y-4" data-testid="bk-bookings-tab">
      {/* Working hours config (Iter27 G1) */}
      <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="bk-working-hours">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} weight="duotone" className="text-[#0ABAB5]" />
          <p className="text-sm font-semibold text-white">Orari i punës</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1 block">Hapja</label>
            <Input type="number" min="0" max="23" defaultValue={workingHours.open_hour}
                   onBlur={(e) => {
                     const v = parseInt(e.target.value, 10);
                     if (!Number.isNaN(v) && v !== workingHours.open_hour) saveWorkingHours({ open_hour: v });
                   }}
                   className="h-9 bg-[#050505] border-white/10 rounded-lg text-sm text-right"
                   data-testid="bk-open-hour-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1 block">Mbyllja</label>
            <Input type="number" min="1" max="24" defaultValue={workingHours.close_hour}
                   onBlur={(e) => {
                     const v = parseInt(e.target.value, 10);
                     if (!Number.isNaN(v) && v !== workingHours.close_hour) saveWorkingHours({ close_hour: v });
                   }}
                   className="h-9 bg-[#050505] border-white/10 rounded-lg text-sm text-right"
                   data-testid="bk-close-hour-input" />
          </div>
        </div>
        <p className="text-[10px] text-white/45 mt-2">Hënë–Shtunë e disponueshme (E Diele e mbyllur).</p>
      </div>

      {/* Calendar config (slot interval + 7-day mini cal) */}
      <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="bk-slot-config">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} weight="duotone" className="text-[#0ABAB5]" />
            <p className="text-sm font-semibold text-white">Intervali i sllotit</p>
          </div>
          <div className="flex gap-1 p-1 bg-[#050505] border border-white/10 rounded-xl flex-wrap">
            {SLOT_CHOICES.map(({ m, label }) => (
              <button key={m} onClick={() => saveSlot(m)} data-testid={`bk-slot-${m}`}
                      className={`px-3 h-7 rounded-lg text-xs font-bold ${slotMinutes === m ? 'bg-[#0ABAB5] text-black' : 'text-white/55'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* 7-day clickable mini-calendar */}
        <div className="grid grid-cols-7 gap-1.5">
          {weekDays.map((d) => {
            const key = dayKey(d);
            const list = baseVisible.filter((b) => (b.scheduled_at || '').slice(0, 10) === key);
            const isToday = key === todayKey;
            const isActive = selectedDay === key;
            // Iter27 G1 — visually hint Sunday (working_days excludes it by default).
            const isWorkingDay = workingHours.working_days?.includes(d.getDay() === 0 ? 7 : d.getDay());
            return (
              <button key={key}
                      onClick={() => setSelectedDay(isActive ? null : key)}
                      data-testid={`bk-week-day-${key}`}
                      className={`p-2 rounded-xl text-center transition ${
                        isActive
                          ? 'bg-[#0ABAB5]/30 border border-[#0ABAB5]'
                          : isToday
                            ? 'bg-[#0ABAB5]/10 border border-[#0ABAB5]/40'
                            : 'bg-white/[0.03] border border-transparent hover:bg-white/[0.06]'
                      } ${!isWorkingDay ? 'opacity-40' : ''}`}>
                <p className="text-[10px] uppercase text-white/45">{d.toLocaleDateString('sq-AL', { weekday: 'short' })}</p>
                <p className="text-base font-bold text-white">{d.getDate()}</p>
                {list.length > 0 ? (
                  <p className="text-[10px] text-[#FF6B35] font-semibold mt-1">{list.length} rez.</p>
                ) : (
                  <p className="text-[10px] text-white/30 mt-1">{isWorkingDay ? 'E lirë' : '—'}</p>
                )}
              </button>
            );
          })}
        </div>
        {selectedDay && (
          <button onClick={() => setSelectedDay(null)} data-testid="bk-day-filter-clear"
                  className="mt-2 text-[10px] text-[#0ABAB5] underline">
            Hiq filtrin e datës
          </button>
        )}
      </div>

      {/* Hide-completed toggle (Iter27 G4) */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-[#0a0a0a] border border-white/5">
        <div className="flex items-center gap-2">
          <EyeSlash size={14} className="text-white/55" />
          <p className="text-xs text-white/70">Fshih të kryerat</p>
        </div>
        <button onClick={() => setHideCompleted(!hideCompleted)} data-testid="bk-hide-completed-toggle"
                className={`relative w-10 h-6 rounded-full ${hideCompleted ? 'bg-[#0ABAB5]' : 'bg-white/15'}`}>
          <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition ${hideCompleted ? 'translate-x-4' : ''}`} />
        </button>
      </div>

      {/* Bookings list */}
      <div className="space-y-2.5">
        {visible.length === 0 ? (
          <div className="p-6 text-center rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-sm text-white/50">Asnjë rezervim aktiv</p>
          </div>
        ) : visible.map((b) => (
          <motion.div key={b.booking_id}
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5"
                      data-testid={`bk-booking-${b.booking_id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-1">
                  {b.service_name_snapshot?.toLowerCase().includes('liquid') ? (
                    <Sparkle size={14} weight="fill" className="text-[#FF6B35]" />
                  ) : (
                    <Drop size={14} weight="fill" className="text-[#7C3AED]" />
                  )}
                  <p className="text-sm font-semibold text-white truncate"
                     data-testid={`bk-booking-svc-${b.booking_id}`}>
                    {b.service_name_snapshot}
                  </p>
                </div>
                <p className="text-xs text-white/75 flex items-center gap-1.5">
                  <User size={11} weight="duotone" /> {b.customer_name || '—'}
                  {b.customer_phone && (
                    <>
                      <span className="text-white/30">·</span>
                      <Phone size={11} weight="duotone" /> {b.customer_phone}
                    </>
                  )}
                </p>
                <p className="text-xs text-white/55 flex items-center gap-1.5 mt-0.5">
                  <Clock size={11} /> {new Date(b.scheduled_at).toLocaleString('sq-AL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${STATUS_COLOR(b.status)}`}
                      data-testid={`bk-booking-status-${b.booking_id}`}>
                  {STATUS_LABEL(b.status)}
                </span>
                <button onClick={() => deleteBooking(b)}
                        data-testid={`bk-booking-delete-${b.booking_id}`}
                        className="w-7 h-7 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center">
                  <Trash size={12} weight="fill" />
                </button>
              </div>
            </div>
            {/* Staff assign */}
            <div className="flex items-center gap-2 mt-2">
              <label className="text-[10px] uppercase tracking-wider text-white/45 shrink-0">Punonjësi:</label>
              <select value={b.staff_id || ''} onChange={(e) => reassign(b, e.target.value)}
                      data-testid={`bk-booking-assign-${b.booking_id}`}
                      className="flex-1 h-8 bg-[#050505] border border-white/10 rounded-lg text-xs px-2 text-white">
                <option value="">— pa cakto —</option>
                {staff.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.name} {s.role === 'location_manager' ? '(Mgr)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {/* Actions */}
            <div className="flex gap-2 mt-3">
              {isActiveStatus(b.status) && (
                <Button size="sm" onClick={() => completeBooking(b)}
                        data-testid={`bk-booking-advance-${b.booking_id}`}
                        className="flex-1 h-8 bg-[#0ABAB5]/15 text-[#0ABAB5] hover:bg-[#0ABAB5]/25 text-xs rounded-lg">
                  Shëno si Kryer
                </Button>
              )}
              {b.status === 'completed' && b.payment_status !== 'paid' && (
                <Button size="sm" onClick={() => openPay(b)}
                        data-testid={`bk-booking-pay-${b.booking_id}`}
                        className="flex-1 h-8 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-xs rounded-lg">
                  <CurrencyEur size={12} className="mr-1" /> Shëno si paguar
                </Button>
              )}
              {b.payment_status === 'paid' && (
                <span className="flex-1 h-8 flex items-center justify-center text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 rounded-lg"
                      data-testid={`bk-booking-paid-${b.booking_id}`}>
                  <CheckCircle size={11} weight="fill" className="mr-1" />
                  Paguar {b.paid_amount?.toLocaleString?.()} {b.paid_currency}
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Mark-paid dialog — currency is auto (Iter27 E) */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent aria-describedby={undefined} className="bg-[#121212] border-white/10 max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-['Outfit']">Shëno si paguar</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-white/55">{payTarget?.service_name_snapshot} · {payTarget?.customer_name}</p>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/45">
                Vlera ({premiumCurrency})
              </label>
              <Input type="number" step="0.01" value={payAmount}
                     onChange={(e) => setPayAmount(e.target.value)}
                     className="h-10 bg-[#050505] border-white/10 rounded-xl text-sm"
                     data-testid="bk-pay-amount-input" />
              <p className="text-[10px] text-white/40 mt-1">Monedha caktohet automatikisht nga Opsionet ({premiumCurrency}).</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={submitPay} data-testid="bk-pay-submit-btn"
                      className="flex-1 h-10 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 rounded-xl text-sm">
                Konfirmo
              </Button>
              <Button variant="outline" onClick={() => setPayTarget(null)}
                      className="h-10 border-white/15 text-white/70 rounded-xl text-sm">
                Anulo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
}
